import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { WizardLayout } from '../components/WizardLayout';
import { api, type OrderStatus } from '../lib/api';

/**
 * Reached when the user cancels Stripe Checkout (or refreshes during it).
 * We show the order summary and let them retry payment.
 *
 * Most users never see this – they go straight from /auth-code to Stripe to
 * /success. This is the recovery path.
 */
export function Pay() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const cancelled = params.get('cancelled') === '1';
  const [order, setOrder] = useState<OrderStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;
    api.getOrder(orderId).then(setOrder).catch((e) => setError(e.message));
  }, [orderId]);

  return (
    <WizardLayout step="pay">
      <div className="card">
        <h1 className="text-2xl font-semibold text-slate-900">Payment</h1>
        {cancelled && (
          <div className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
            Payment was cancelled. Your order is still here – try again when you're ready.
          </div>
        )}
        {error && <div className="mt-4 text-red-700">{error}</div>}
        {order && (
          <div className="mt-6 space-y-3 text-sm">
            <Row label="Company" value={`${order.companyName} (${order.companyNumber})`} />
            <Row label="Period end" value={order.periodEnd} />
            <Row label="Amount" value={`£${(order.amountPence / 100).toFixed(2)}`} />
            <Row label="Status" value={order.status} />
            <p className="pt-4 text-xs text-slate-500">
              To retry payment, please go back to the start of the wizard and re-enter your
              details. (Resuming payment from this page is coming soon.)
            </p>
          </div>
        )}
      </div>
    </WizardLayout>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between border-b border-slate-100 py-2">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
